import React from "react";
var __jsx = React.createElement;
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UnstyledButton } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRectangleList, faUserTie, faFileChartColumn, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useAuth } from "@qure/qure-auth";
import { useTaskLabels } from "@/hooks/tasks";
import { useWorkspaceUsers } from "@/hooks/use-workspaces";
import { FormSelect, FormDate } from "@/components/form";
import { LinkPatientRecordsModal } from "@/components/link-patient-records-modal";
import { countLinkedResources } from "@/components/patient-tasks";
import { DueDateShortcuts } from "./due-date-shortcuts";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
/** Controlled row of editable metadata fields: label, owner, due date, and linked resources. */
export function TaskDetailMeta(_ref) {
  var _userInfo$default_wor;
  var reminderId = _ref.reminderId,
    labelId = _ref.labelId,
    ownedBy = _ref.ownedBy,
    dueDate = _ref.dueDate,
    linkedResources = _ref.linkedResources,
    onLabelChange = _ref.onLabelChange,
    onOwnerChange = _ref.onOwnerChange,
    onDueDateChange = _ref.onDueDateChange,
    onLinkedChange = _ref.onLinkedChange;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useAuth = useAuth(),
    userInfo = _useAuth.userInfo;
  var workspaceId = (_userInfo$default_wor = userInfo === null || userInfo === void 0 ? void 0 : userInfo.default_workspace) !== null && _userInfo$default_wor !== void 0 ? _userInfo$default_wor : null;
  var _useState = useState(false),
    isLinkRecordsOpen = _useState[0],
    setIsLinkRecordsOpen = _useState[1];
  var _useTaskLabels = useTaskLabels(workspaceId),
    _useTaskLabels$data = _useTaskLabels.data,
    taskLabels = _useTaskLabels$data === void 0 ? [] : _useTaskLabels$data;
  var _useWorkspaceUsers = useWorkspaceUsers(workspaceId),
    _useWorkspaceUsers$da = _useWorkspaceUsers.data,
    workspaceUsers = _useWorkspaceUsers$da === void 0 ? [] : _useWorkspaceUsers$da;
  var labelOptions = taskLabels.map(function (l) {
    return {
      value: String(l.id),
      label: l.name
    };
  });
  var ownerOptions = workspaceUsers.map(function (u) {
    return {
      value: String(u.id),
      label: u.name
    };
  });
  var attachmentCount = countLinkedResources(linkedResources);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "grid grid-cols-2 gap-x-2 gap-y-3"
  }, /*#__PURE__*/React.createElement(FormSelect, {
    "data-testid": "task-detail-label",
    icon: /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faRectangleList,
      className: "w-3.5 h-3.5"
    }),
    placeholder: t("label.select_task_type"),
    data: labelOptions,
    value: labelId,
    onChange: function onChange(value) {
      trackEvent(ANALYTICS_EVENTS.TASK_DETAIL_LABEL, {
        taskId: reminderId
      });
      onLabelChange(value);
    },
    clearable: true,
    classNames: {
      root: "min-w-0"
    }
  }), /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "task-detail-linked-resources",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.TASK_DETAIL_LINKED_RESOURCES, {
        taskId: reminderId
      });
      setIsLinkRecordsOpen(true);
    },
    className: "w-full flex items-center justify-between px-2.5 rounded-sds-200 border border-sds-base-default text-style-label-base text-sds-base-default hover:bg-sds-base-secondary transition-colors"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex-shrink-0"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faFileChartColumn,
    className: "w-4 h-4 mr-2"
  }), /*#__PURE__*/React.createElement("span", null, attachmentCount, " ", t("linked"))), /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faPlus,
    className: "w-4 h-4 flex-shrink-0 text-sds-base-tertiary"
  })), /*#__PURE__*/React.createElement("div", {
    className: "min-w-0 flex flex-col gap-2"
  }, /*#__PURE__*/React.createElement(FormDate, {
    value: dueDate,
    onChange: function onChange(value) {
      trackEvent(ANALYTICS_EVENTS.TASK_DETAIL_DUE_DATE, {
        taskId: reminderId
      });
      onDueDateChange(value);
    },
    testId: "task-detail-due-date",
    highlightToday: true
  }), dueDate && /*#__PURE__*/React.createElement(DueDateShortcuts, {
    currentDate: dueDate,
    onBump: onDueDateChange
  })), /*#__PURE__*/React.createElement(FormSelect, {
    "data-testid": "task-detail-owner",
    icon: /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faUserTie,
      className: "w-3.5 h-3.5"
    }),
    placeholder: t("label.task_owner"),
    data: ownerOptions,
    value: ownedBy,
    onChange: function onChange(value) {
      trackEvent(ANALYTICS_EVENTS.TASK_DETAIL_OWNER, {
        taskId: reminderId
      });
      onOwnerChange(value);
    },
    searchable: true,
    clearable: true,
    classNames: {
      root: "min-w-0 self-start"
    }
  })), /*#__PURE__*/React.createElement(LinkPatientRecordsModal, {
    show: isLinkRecordsOpen,
    onClose: function onClose() {
      return setIsLinkRecordsOpen(false);
    },
    onLink: function onLink(resources) {
      onLinkedChange(resources);
      setIsLinkRecordsOpen(false);
    },
    linkedRes: linkedResources
  }));
}